/*
 * Page statut publique (/statut).
 * Transparence : montre aux visiteuses si le service fonctionne normalement,
 * si une pause est en cours (avec l'heure estimée de retour) ou si un
 * incident technique est détecté. Aucune donnée sensible n'est affichée.
 * Cette page reste accessible même pendant le mode maintenance.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  CheckCircle2,
  Clock3,
  Database,
  PauseCircle,
  RefreshCw,
  TriangleAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchServiceStatus } from "@/lib/api";

/* Apparence de chaque état : couleurs issues de la palette existante. */
const STATE_STYLES = {
  operationnel: {
    icon: CheckCircle2,
    badge: "bg-[#EAF7E7] text-[#114E0B] border-[rgba(17,78,11,0.2)]",
    iconWrap: "bg-[#EAF7E7] text-[#114E0B]",
    title: "Tout fonctionne normalement",
    description:
      "Le diagnostic, le bilan et l'envoi par email sont pleinement disponibles.",
    label: "Service opérationnel",
  },
  pause: {
    icon: PauseCircle,
    badge: "bg-[#E2F6FF] text-[#235277] border-[rgba(35,82,119,0.25)]",
    iconWrap: "bg-[#E2F6FF] text-[#235277]",
    title: "CYCLIQ est en pause",
    description:
      "Une amélioration est en cours. Le service revient très vite, rien n'est perdu.",
    label: "Pause en cours",
  },
  degrade: {
    icon: TriangleAlert,
    badge: "bg-[#FBEBEA] text-[#8C2F2B] border-[rgba(140,47,43,0.25)]",
    iconWrap: "bg-[#FBEBEA] text-[#8C2F2B]",
    title: "Petit souci technique",
    description:
      "Une partie du service répond mal en ce moment. Nos équipes sont dessus, réessaie dans quelques minutes.",
    label: "Service perturbé",
  },
};

/* Libellé français de l'heure de retour estimée. */
const formatResumeLabel = (resumeAt) => {
  const resumeDate = resumeAt ? new Date(resumeAt) : null;
  if (!resumeDate || Number.isNaN(resumeDate.getTime())) return "";
  const time = resumeDate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  if (resumeDate.toDateString() === new Date().toDateString()) {
    return `aujourd'hui à ${time}`;
  }
  const day = resumeDate.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  return `${day} à ${time}`;
};

const StatusLoadingState = () => (
  <div className="space-y-4" data-testid="status-loading">
    <Skeleton className="h-14 w-14 rounded-full mx-auto" />
    <Skeleton className="h-6 w-2/3 mx-auto rounded" />
    <Skeleton className="h-4 w-5/6 mx-auto rounded" />
    <Skeleton className="h-12 w-full rounded-xl" />
  </div>
);

const StatusErrorState = () => {
  const style = STATE_STYLES.degrade;
  const Icon = style.icon;
  return (
    <div className="text-center" data-testid="status-error">
      <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${style.iconWrap}`} aria-hidden="true">
        <Icon className="h-6 w-6" />
      </div>
      <h1 className="font-heading mt-5 text-xl font-semibold tracking-tight text-[#060606]">
        Impossible de vérifier le statut
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-[#333333]">
        La vérification n'a pas abouti. Cela peut venir de ta connexion ou d'un
        souci passager de notre côté. Réessaie dans un instant.
      </p>
    </div>
  );
};

const StatusResult = ({ data }) => {
  const style = STATE_STYLES[data.status] || STATE_STYLES.degrade;
  const Icon = style.icon;
  const resumeLabel = data.maintenance?.enabled ? formatResumeLabel(data.maintenance.resume_at) : "";
  return (
    <div className="text-center">
      <div
        className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${style.iconWrap}`}
        aria-hidden="true"
      >
        <Icon className="h-6 w-6" />
      </div>
      <span
        className={`mt-4 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${style.badge}`}
        data-testid="status-badge"
      >
        <Activity className="h-3 w-3" aria-hidden="true" />
        {style.label}
      </span>
      <h1 className="font-heading mt-4 text-xl font-semibold tracking-tight text-[#060606]" data-testid="status-title">
        {style.title}
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-[#333333]" data-testid="status-description">
        {style.description}
      </p>

      {resumeLabel ? (
        <div
          className="mt-5 rounded-xl border border-[rgba(35,82,119,0.14)] bg-[rgba(226,246,255,0.45)] px-4 py-3"
          data-testid="status-resume-info"
        >
          <div className="flex items-center justify-center gap-1.5 text-[#235277]">
            <Clock3 className="w-3.5 h-3.5" aria-hidden="true" />
            <p className="text-[11px] font-semibold uppercase tracking-wide">Retour estimé</p>
          </div>
          <p className="mt-1 font-heading text-base font-semibold text-[#235277]">{resumeLabel}</p>
        </div>
      ) : null}

      <div
        className="mt-5 flex items-center justify-center gap-2 rounded-xl border border-[rgba(35,82,119,0.14)] bg-[rgba(226,246,255,0.35)] px-4 py-3"
        data-testid="status-database-row"
      >
        <Database className="w-3.5 h-3.5 text-[#235277]" aria-hidden="true" />
        <span className="text-xs font-medium text-[#333333]">Stockage technique</span>
        <span
          className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md ${
            data.database_ok ? "bg-[#EAF7E7] text-[#114E0B]" : "bg-[#FBEBEA] text-[#8C2F2B]"
          }`}
          data-testid="status-database-state"
        >
          {data.database_ok ? "Disponible" : "Indisponible"}
        </span>
      </div>
    </div>
  );
};

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [checkedAt, setCheckedAt] = useState(null);

  const check = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await fetchServiceStatus();
      setData(result);
      setCheckedAt(new Date());
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  return (
    <div
      data-testid="status-page"
      className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#F9FEFF]"
    >
      <div className="w-full max-w-[440px]">
        <div className="rounded-2xl bg-white border border-[rgba(35,82,119,0.14)] shadow-[var(--shadow-md)] px-8 py-10">
          {loading && <StatusLoadingState />}
          {!loading && error && <StatusErrorState />}
          {!loading && !error && data && <StatusResult data={data} />}

          {checkedAt && !loading ? (
            <p className="mt-4 text-center text-[11px] text-[#9E9E9E]" data-testid="status-checked-at">
              Vérifié le {checkedAt.toLocaleDateString("fr-FR")} à{" "}
              {checkedAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </p>
          ) : null}

          <Button
            onClick={check}
            disabled={loading}
            variant="outline"
            data-testid="status-refresh-button"
            className="mt-6 w-full min-h-[44px] rounded-xl border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] font-heading font-semibold transition-colors duration-200"
          >
            <RefreshCw className={`mr-2 w-4 h-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
            {loading ? "Vérification" : "Vérifier à nouveau"}
          </Button>

          <Link
            to="/"
            data-testid="status-back-home-link"
            className="mt-4 flex items-center justify-center gap-1.5 text-xs font-medium text-[#235277] underline underline-offset-2 hover:text-[#1B415F] transition-colors duration-200"
          >
            <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
            Retour à l'accueil
          </Link>
        </div>
        <p className="mt-5 text-center text-[11px] text-[#9E9E9E]">Gino Body & Soul</p>
      </div>
    </div>
  );
}
